import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { DirectoryFilters } from '../components/directory/DirectoryFilters';
import { DirectoryGrid } from '../components/directory/DirectoryGrid';
import { Pagination } from '../components/directory/Pagination';
import { getAllCommunityMembers } from '../services/communityService';
import { CommunityMember, CommunityQueryParams } from '../types/community';
import { SparklesIcon, ArrowRightIcon, SearchIcon, PackageIcon, HandshakeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
export function BrandsDirectoryPage() {
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [queryParams, setQueryParams] = useState<CommunityQueryParams>({
    page: 1,
    limit: 12,
    type: 'brand'
  });
  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const {
          data,
          totalPages
        } = await getAllCommunityMembers(queryParams);
        setMembers(data);
        setTotalPages(totalPages);
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [queryParams]);
  const handleFilterChange = (newParams: Partial<CommunityQueryParams>) => {
    setQueryParams(prev => ({
      ...prev,
      ...newParams,
      page: 1
    }));
  };
  const handlePageChange = (page: number) => {
    setQueryParams(prev => ({
      ...prev,
      page
    }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQueryParams(prev => ({
      ...prev,
      search: searchQuery,
      page: 1
    }));
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1666">
      <Navbar data-id="element-1667" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-16" data-id="element-1668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1669">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1670">
            <div className="inline-flex items-center px-3 py-1 bg-blue-500/30 text-blue-100 rounded-full text-sm font-medium mb-4" data-id="element-1671">
              <PackageIcon className="h-4 w-4 mr-2" data-id="element-1672" />
              Brand Directory
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1673">
              Discover Innovative Brands
            </h1>
            <p className="text-lg text-blue-100 mb-8" data-id="element-1674">
              Connect with brands looking for event partnerships and sampling
              opportunities. Find your perfect match based on audience,
              industry, and marketing goals.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-6" data-id="element-1675">
              <div className="relative" data-id="element-1676">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1677" />
                <input type="text" placeholder="Search brands by name, industry, or keywords..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border-0 shadow-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300" data-id="element-1678" />
              </div>
            </form>

            <div className="flex flex-wrap justify-center gap-4" data-id="element-1679">
              <Link to="/onboarding/organizer" data-id="element-1680">
                <Button variant="outline" className="bg-white text-blue-600 border-white hover:bg-blue-50" data-id="element-1681">
                  <SparklesIcon className="h-5 w-5 mr-2" data-id="element-1682" />
                  I'm an Organizer
                </Button>
              </Link>
              <Link to="/organizers" data-id="element-1683">
                <Button variant="ghost" className="text-white border-white/30 hover:bg-white/10" data-id="element-1684">
                  Browse Organizers
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1685" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Section */}
      <section className="py-12" data-id="element-1686">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1687">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-1688">
            {/* Filters Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0" data-id="element-1689">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24" data-id="element-1690">
                <h3 className="font-semibold text-gray-900 mb-4" data-id="element-1691">Filters</h3>
                <DirectoryFilters onFilterChange={handleFilterChange} currentFilters={queryParams} memberType="brand" data-id="element-1692" />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-1693">
              <div className="flex items-center justify-between mb-6" data-id="element-1694">
                <div data-id="element-1695">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1696">
                    Browse Brands
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1697">
                    {loading ? 'Loading...' : `${members.length} brands found`}
                  </p>
                </div>
              </div>

              {loading ? <div className="flex justify-center items-center h-64" data-id="element-1698">
                  <div className="flex flex-col items-center" data-id="element-1699">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4" data-id="element-1700"></div>
                    <p className="text-gray-500" data-id="element-1701">Loading brands...</p>
                  </div>
                </div> : members.length === 0 ? <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center" data-id="element-1702">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1703">
                    <PackageIcon className="h-8 w-8 text-gray-400" data-id="element-1704" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1705">
                    No brands found
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-1706">
                    Try adjusting your filters or search criteria to find more
                    brands.
                  </p>
                  <Button variant="outline" onClick={() => {
                setSearchQuery('');
                setQueryParams({
                  page: 1,
                  limit: 12,
                  type: 'brand'
                });
              }} data-id="element-1707">
                    Clear Filters
                  </Button>
                </div> : <>
                  <DirectoryGrid members={members} data-id="element-1708" />
                  {totalPages > 1 && <div className="mt-8" data-id="element-1709">
                      <Pagination currentPage={queryParams.page || 1} totalPages={totalPages} onPageChange={handlePageChange} data-id="element-1710" />
                    </div>}
                </>}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-gray-100" data-id="element-1711">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1712">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1713">
            Are you a brand looking for event partnerships?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1714">
            Join SponsrAI to get matched with event organizers that align with
            your target audience and marketing goals.
          </p>
          <Link to="/onboarding/brand" data-id="element-1715">
            <Button size="lg" rightIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-1717" />} data-id="element-1716">
              Register Your Brand
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8" data-id="element-1718">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1719">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-1720">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-1721">
              <div className="bg-blue-600 p-2 rounded-lg mr-3" data-id="element-1722">
                <HandshakeIcon className="h-5 w-5 text-white" data-id="element-1723" />
              </div>
              <span className="text-white font-semibold" data-id="element-1724">SponsrAI</span>
            </div>
            <div className="text-sm" data-id="element-1725">
              © {new Date().getFullYear()} SponsrAI. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>;
}